/**
 * FeedbackTab.js — Client feedback viewer by cycle
 * Extracted from AdminDashboard.js during Phase 1 refactor
 */

import React, { useState, useEffect, useCallback } from "react";
import S from "./adminStyles";
import { exportToCSV, printTable } from "../../utils/exportUtils";
import { formatCycleDate } from "../../utils/dateUtils";

const NAVY = "#1A3B6E";
const LIGHT = "#E8F2FB";

export default function FeedbackTab({ user }) {
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState(null); // null = awaiting init, "" = all cycles
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cycleLoading, setCycleLoading] = useState(true);

  // Load all cycles on mount, default to active/open cycle
  useEffect(() => {
    let active = true;
    const loadCycles = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch("/api/cycles", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const body = await res.json();
          const list = (body.cycles || []).filter(c => c.status !== "draft");
          if (active) {
            setCycles(list);
            const cur = list.find(c => c.status === "open" || c.status === "active");
            setCycleId(cur ? (cur.cycleId || cur.id) : "");
          }
        } else {
          if (active) setCycleId("");
        }
      } catch (err) {
        console.error("Cycles load error:", err);
        if (active) setCycleId("");
      } finally {
        if (active) setCycleLoading(false);
      }
    };
    loadCycles();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const idToken = await user.getIdToken();
      const url = cycleId ? `/api/feedback?cycleId=${cycleId}` : "/api/feedback";
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFeedback(data.feedback || data || []);
      } else {
        setFeedback([]);
      }
    } catch (err) {
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  }, [cycleId, user]);

  useEffect(() => {
    if (cycleId !== null && !cycleLoading) {
      loadFeedback();
    }
  }, [loadFeedback, cycleId, cycleLoading]);

  const FEEDBACK_COLS = [
    { key: "submittedAt", label: "Submitted At" },
    { key: "tokenId", label: "Token ID" },
    { key: "rating", label: "Rating" },
    { key: "comment", label: "Comment" },
  ];

  const feedbackRows = () =>
    feedback.map((fb) => ({
      ...fb,
      submittedAt: fb.submittedAt
        ? new Date(
            fb.submittedAt._seconds ? fb.submittedAt._seconds * 1000 : fb.submittedAt
          ).toISOString()
        : "",
    }));

  const exportFeedbackCSV = () => {
    const today = new Date().toISOString().split("T")[0];
    exportToCSV(`raider-depot-feedback-${today}.csv`, feedbackRows(), FEEDBACK_COLS);
  };

  const printFeedback = () => {
    printTable("Feedback", `${feedback.length} entries`, FEEDBACK_COLS, feedbackRows());
  };

  const cycleLabel = cycleId
    ? `for cycle ${formatCycleDate(cycleId)}`
    : "across all cycles";

  return (
    <>
      {cycleLoading ? (
        <div style={S.card}>
          <p style={{ color: "#64748b" }}>Loading cycles...</p>
        </div>
      ) : (
        <>
          <div style={S.card}>
            <div style={S.flexRowBetween}>
              <h2 style={S.cardTitle}>Client Feedback</h2>
              <div style={S.flexRow}>
                <select
                  value={cycleId ?? ""}
                  onChange={e => setCycleId(e.target.value)}
                  style={{
                    border: `1px solid ${LIGHT}`,
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontSize: 13,
                    background: "#FFFFFF",
                    color: NAVY,
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="">All Cycles</option>
                  {cycles.map(c => {
                    const cid = c.cycleId || c.id;
                    return (
                      <option key={cid} value={cid}>
                        {formatCycleDate(cid)} — {c.status}
                      </option>
                    );
                  })}
                </select>
                <button style={S.btnPrimary} onClick={loadFeedback}>
                  Load
                </button>
                <button style={S.btnSecondary} onClick={exportFeedbackCSV} disabled={feedback.length === 0}>
                  Export CSV
                </button>
                <button style={S.btnSecondary} onClick={printFeedback} disabled={feedback.length === 0}>
                  Print
                </button>
              </div>
            </div>

            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              {loading
                ? "Loading..."
                : `${feedback.length} feedback entr${feedback.length === 1 ? "y" : "ies"} ${cycleLabel}`}
            </div>

            {!loading && feedback.length > 0 && (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Token ID</th>
                    <th style={S.th}>Rating</th>
                    <th style={S.th}>Comment</th>
                    <th style={S.th}>Cycle</th>
                    <th style={S.th}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {feedback.map((fb, i) => (
                    <tr key={fb.id || i}>
                      <td style={S.td}>
                        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                          {fb.tokenId || "—"}
                        </span>
                      </td>
                      <td style={S.td}>
                        <span
                          style={S.badge(
                            fb.rating >= 4
                              ? "#16a34a"
                              : fb.rating >= 3
                                ? "#f59e0b"
                                : "#dc2626",
                          )}
                        >
                          {fb.rating}/5
                        </span>
                      </td>
                      <td style={{ ...S.td, maxWidth: 360 }}>
                        <span style={{ fontSize: 13, color: "#334155" }}>
                          {fb.comment || "—"}
                        </span>
                      </td>
                      <td style={S.td}>{fb.cycleId || cycleId || "—"}</td>
                      <td style={S.td}>
                        {fb.submittedAt
                          ? new Date(
                              fb.submittedAt._seconds
                                ? fb.submittedAt._seconds * 1000
                                : fb.submittedAt,
                            ).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && feedback.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#94a3b8",
                  padding: "40px 0",
                  fontSize: 14,
                }}
              >
                {cycleId
                  ? "No feedback submitted yet for this cycle."
                  : "No feedback submitted yet."}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
